import { Component, OnDestroy, OnInit } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { DriverSummaryPanelComponent } from "@app/directives/_vehicle/driverSummaryPanel/driverSummaryPanel.component";
import { DateRange } from "@app/enum/DateRange.enum";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { DateUnit } from "@app/services/date.service";
import { TranslationService } from "@app/services/translation.service";
import { lastValueFrom, Subscription } from "rxjs";
import { TmsModuleNavigation } from "../../tms.navigation";
import { RoutePerformanceBarComponent } from "../../_components/route-performance-bar/route-performance-bar.component";
import { RouteStatus } from "../../_enums/RouteStatus";
import { RouteVisitStatus } from "../../_enums/RouteVisitStatus";
import { IPlannedRouteOptions } from "../../_models/IPlannedRouteOptions";
import { IPlannedRouteDetail, PlannedRouteService } from "../../_services/planned-route.service";
import { PlannedRouteService2 } from "../../_services/planned-route.service2";
import { ActiveRouteProgressComponent } from "../activeRoutes/active-route-progress/active-route-progress.component";
import './tms-history.component.scss';

@Component({
    selector: "tms-history",
    template: require("./tms-history.component.html"),
})
export class TmsHistoryComponent implements OnInit, OnDestroy {

    defaultDateRange = DateRange.Last7Days;
    filterEvent: IAggregateFilterEvent<IPlannedRouteDetail>;
    plannedRoutes: IPlannedRouteDetail[] = [];
    selectedRoute: IPlannedRouteDetail;
    filterTabs: IFilterBarTab<IPlannedRouteDetail>[] = [];
    loading = true;
    SortType = SortType;
    exportTitle = this.translationService.t("Tms.TmsHistory");
    DateUnit = DateUnit;
    plannedRoutesSubscription: Subscription;

    //// #Tables
    tableColumns: IVTableColumn<IPlannedRouteDetail>[];
    tableRows: IVTableRow[];

    constructor(
        private readonly plannedRouteService: PlannedRouteService,
        private readonly plannedRouteService2: PlannedRouteService2,
        private readonly translationService: TranslationService,
        private readonly vTableService: VTableService
    ) {
        this.createTabs();
    }

    ngOnInit(): void {
        this.plannedRoutesSubscription = this.plannedRouteService2.plannedRoutesSubject.subscribe((routes) => {
            this.plannedRoutes = routes;
            this.createTabs();
            this.createTable();
            this.tableRows = this.vTableService.createTableRows(this.plannedRoutes, this.tableColumns);
            
            this.loading = false;
        });
    }

    onFilterChanged(event: IAggregateFilterEvent<any>): void {
        if (!event.trigger) {
            return;
        }
        this.filterEvent = event;

        if (this.filterEvent.trigger == "date") {
            this.requestData(true);
        }
    }

    selectedRouteChanged(route: IPlannedRouteDetail): void {
        this.selectedRoute = route;
        this.plannedRouteService.openRouteDetailsDialog(route, TmsModuleNavigation.History);
        this.selectedRoute = null;
    }

    async requestData(invalidateCache = false): Promise<void> {
        this.loading = true;
        this.plannedRoutes = [];

        const options: IPlannedRouteOptions = {
            mapDrivers: true,
            mapVehicles: true,
            updateDistance: false,
            invalidateCache: invalidateCache,
            customTimes: true,
            routeDuration: true,
            routeDisplayName: true,
            startedRoutesOnly: true,
            routeDisplayStatus: true
        };

        await lastValueFrom(this.plannedRouteService2.requestPlannedRoutes(this.filterEvent.date[0], this.filterEvent.date[1], options));
    }

    private createTable() {

        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: 'TmsHistory.Completed', sortType: SortType.Number, width: 120 }),
                createCellFunction: (plannedRoute) => this.vTableService.createTableCell(plannedRoute, { exportData: `${this.routeProgressExportData(plannedRoute)}%`, sortData: this.routeProgressExportData(plannedRoute), component: ActiveRouteProgressComponent, componentInputs: { route: plannedRoute } }),
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: 'TmsHistory.Start', sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: (plannedRoute) => this.vTableService.createDateCell(plannedRoute.PlannedStartTime),
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: 'TmsHistory.End', minTableWidth: 1400 }),
                createCellFunction: (plannedRoute) => this.vTableService.createDateCell(plannedRoute.PlannedEndTime),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'TmsHistory.Duration', minTableWidth: 1400, sortType: SortType.Number }),
                createCellFunction: (plannedRoute) => this.vTableService.createDurationCell(plannedRoute.totalDurationMins, DateUnit.Min),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'TmsHistory.Route' }),
                createCellFunction: (plannedRoute) => this.vTableService.createTableCell(plannedRoute.routeDisplayName),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'TmsHistory.DriverVehicle' }),
                createCellFunction: (plannedRoute) => this.vTableService.createTableCell(plannedRoute, { exportData: this.routeVehicleDriverExportData(plannedRoute), sortData: this.routeVehicleDriverExportData(plannedRoute), component: DriverSummaryPanelComponent, componentInputs: { driver: plannedRoute.driver, vehicle: plannedRoute.vehicle, compactView: true, showTacho: false } }),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'TmsHistory.ScheduleAdherence', width: 250, sortable: false, excludeFromExport: true }),
                createCellFunction: (plannedRoute) => this.vTableService.createTableCell(plannedRoute, { component: RoutePerformanceBarComponent, componentInputs: { route: plannedRoute } }),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'TmsHistory.Visits', minTableWidth: 1000, width: 80, sortType: SortType.Number }),
                createCellFunction: (plannedRoute) => this.vTableService.createTableCell(this.routeVisitCountData(plannedRoute), { sortData: plannedRoute?.allVisits?.length || 0 }),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'TmsHistory.Status', width: 130, minTableWidth: 900 }),
                createCellFunction: (plannedRoute) => this.vTableService.createEnumCell(RouteStatus, "RouteStatus", plannedRoute.Status)
            }
        ];
    }

    routeVehicleDriverExportData(plannedRoute: IPlannedRouteDetail): string {
        let displayNameLabel = (!plannedRoute.driver?.DisplayName)
            ? this.translationService.t("Tms.NoDriver")
            : plannedRoute.driver.DisplayName;

        let vehicleRegLabel = (!plannedRoute.vehicle?.Reg)
            ? this.translationService.t("Tms.NA")
            : plannedRoute.vehicle.Reg;

        return `${vehicleRegLabel} ${displayNameLabel}`;
    }

    routeProgressExportData(plannedRoute: IPlannedRouteDetail): number {
        if (!plannedRoute?.PlannedVisits) {
            return 0;
        }

        // Note, this should probably use ETA or distance to show how far through we are
        var progress = 0;

        const numCompletedVisits = plannedRoute.PlannedVisits.filter(v => v.Status == RouteVisitStatus.Arrived || v.Status == RouteVisitStatus.Departed).length;
        progress = Math.round(numCompletedVisits * 100 / plannedRoute.PlannedVisits.length);

        return progress;
    }

    routeVisitCountData(plannedRoute: IPlannedRouteDetail): string {

        const visitCount = plannedRoute.allVisits?.length || 0;

        if (!plannedRoute.unplannedVisits?.length) {
            return visitCount.toString();
        }

        return `${visitCount} (${plannedRoute.unplannedVisits.length} ${this.translationService.t("Tms.Unplanned")})`;
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push({ title: this.translationService.t("Common.All"), predicate: () => true });
        this.filterTabs.push({ title: this.translationService.t("Tms.Completed"), predicate: x => x.Status == RouteStatus.Completed || x.Status == RouteStatus.Incomplete || x.Status == RouteStatus.CompletedWithMissing });
        this.filterTabs.push({ title: this.translationService.t("Tms.Incomplete"), predicate: x => x.Status == RouteStatus.Incomplete || x.Status == RouteStatus.CompletedWithMissing });
        this.filterTabs.push({ title: this.translationService.t("Tms.Planned"), predicate: x => x.Status == RouteStatus.Planned });
        this.filterTabs.push({ title: this.translationService.t("Tms.Expired"), predicate: x => x.Status == RouteStatus.Expired || x.Status == RouteStatus.UserExpired });
        this.filterTabs.push({ title: this.translationService.t("Tms.Closed"), predicate: x => x.Status == RouteStatus.Closed });
    }

    ngOnDestroy(): void {
            this.plannedRoutesSubscription?.unsubscribe();
    }
}
